import { FC, KeyboardEvent, MouseEvent, useRef, useState } from "react";
import {
  Control,
  UseFormSetValue,
  FieldError,
  useWatch,
} from "react-hook-form";
import styled from "styled-components";

import { FormDataT } from "./ChannelFormModal";
import { Input, Label, Larger } from "@/components/Forms";
import ValidationMessage from "@/components/Forms/ValidationMessage";
import Tag from "@/components/Tag";
import Text from "@/components/Text";
import { useQuery } from "@tanstack/react-query";
import { ChannelTagResource } from "@/types/resources";
import { api } from "@/utils/api";

const InputWrap = styled.div`
  position: relative;
  ${Input} {
    padding-right: 65px;
  }
`;

const Tags = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 16px;
`;

const AddButton = styled(Text)`
  right: 20px;
  top: 0;
  bottom: 0;
  margin: auto 0;
  position: absolute;
  &:disabled {
    cursor: not-allowed;
  }
`;

const Suggestions = styled.div`
  margin-top: 24px;
`;

const SuggestedTag = styled(Tag)`
  transition: 0.2s ease-out border-color;
  &:hover {
    border-color: ${({ theme }) => theme.colors.black};
  }
`;

interface Props {
  control: Control<FormDataT>;
  setValue: UseFormSetValue<FormDataT>;
  error: FieldError;
}

const ChannelTagsField: FC<Props> = ({ setValue, error, control }) => {
  const { data: suggestions } = useQuery<ChannelTagResource[]>(
    ["channel_tags"],
    () => api.get<never, ChannelTagResource[]>(`/v1/channel-tags`),
    {
      refetchOnReconnect: false,
      staleTime: 120 * 1000,
    }
  );

  const [inputValue, setInputValue] = useState("");

  const inputRef = useRef<HTMLInputElement>(null);

  const tags = useWatch({
    control,
    name: "tags",
  });

  const onTagRemove = (tagToRemove: string) => {
    setValue(
      "tags",
      tags.filter((tag) => tag !== tagToRemove)
    );

    inputRef.current?.focus();
  };

  const addTag = () => {
    if (!inputValue) return;

    if (tags.includes(inputValue)) return;

    setValue("tags", [...tags, inputValue]);

    setInputValue("");
  };

  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key !== "Enter") return;

    e.preventDefault();

    addTag();
  };

  const onSuggestionClick = (e: MouseEvent<HTMLElement>, tag: string) => {
    e.preventDefault();
    e.stopPropagation();

    if (tags.includes(tag)) return;

    setValue("tags", [...tags, tag]);
  };

  return (
    <Larger>
      <Label htmlFor="tagsInput">Channel Tags</Label>
      <InputWrap>
        <Input
          ref={inputRef}
          $hasError={!!error}
          type="text"
          placeholder="Add tags"
          value={inputValue}
          id="tagsInput"
          onKeyDown={onKeyDown}
          onChange={(e) => setInputValue(e.target.value)}
        />
        <AddButton
          as="button"
          $color={inputValue ? "purple" : "gray"}
          $type="caption"
          tabIndex={-1}
          disabled={!inputValue}
          type="button"
          onClick={addTag}
        >
          Add
        </AddButton>
      </InputWrap>
      {!!tags.length && (
        <Tags>
          {tags.map((tag) => (
            <Tag
              key={tag}
              onDelete={(e) => {
                e.stopPropagation();
                onTagRemove(tag);
              }}
            >
              {tag}
            </Tag>
          ))}
        </Tags>
      )}
      {!!error && <ValidationMessage>{error.message}</ValidationMessage>}
      {!!suggestions?.length && (
        <Suggestions>
          <Label as="p">Suggested channel tags</Label>
          <Tags>
            {suggestions
              .filter((suggestion) => !tags.includes(suggestion.title))
              .map((tag) => (
                <SuggestedTag
                  key={tag.id}
                  forwardedAs="button"
                  onClick={(e: MouseEvent<HTMLElement>) =>
                    onSuggestionClick(e, tag.title)
                  }
                >
                  {tag.title}
                </SuggestedTag>
              ))}
          </Tags>
        </Suggestions>
      )}
    </Larger>
  );
};

export default ChannelTagsField;
